import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon, ShieldCheckIcon, ClockIcon, MinusCircleIcon, CheckCircle2Icon, HashIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, US_STATES, StateStatus } from './types';
interface UserLicenseModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  licenses: Record<string, LicenseData>;
  michiganLicense: MichiganLicenseData | undefined;
  onOpenState: (stateName: string) => void;
}
const STATUS_COLORS: Record<StateStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  not_started: {
    bg: 'bg-slate-800/40',
    text: 'text-slate-500',
    border: 'border-slate-700/50'
  },
  in_progress: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/30'
  },
  complete: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30'
  }
};
export function UserLicenseModal({
  user,
  isOpen,
  onClose,
  licenses,
  michiganLicense,
  onOpenState
}: UserLicenseModalProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | StateStatus>('all');
  const statesWithStatus = useMemo(() => {
    return US_STATES.map(state => {
      if (state.name === 'Michigan') {
        const mi = michiganLicense;
        let status: StateStatus = 'not_started';
        if (mi) {
          if (mi.steps.every(Boolean)) status = 'complete';else if (mi.steps.some(Boolean)) status = 'in_progress';
        }
        return {
          ...state,
          status
        };
      }
      const lic = licenses[state.name];
      return {
        ...state,
        status: lic?.status || 'not_started' as StateStatus
      };
    });
  }, [licenses, michiganLicense]);
  const filteredStates = useMemo(() => {
    let result = statesWithStatus;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.abbr.toLowerCase().includes(q));
    }
    if (filter !== 'all') {
      result = result.filter(s => s.status === filter);
    }
    return result;
  }, [statesWithStatus, search, filter]);
  const counts = useMemo(() => {
    let licensed = 0;
    let inProgress = 0;
    for (const s of statesWithStatus) {
      if (s.status === 'complete') licensed++;else if (s.status === 'in_progress') inProgress++;
    }
    return {
      licensed,
      inProgress
    };
  }, [statesWithStatus]);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-349">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-350">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-351" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-352">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800" data-id="element-353">
            <div className="flex items-center gap-3 min-w-0" data-id="element-354">
              <div className="w-10 h-10 rounded-xl bg-slate-700/50 border border-slate-600/50 flex items-center justify-center text-sm font-bold text-slate-300 flex-shrink-0" data-id="element-355">
                {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0" data-id="element-356">
                <h2 className="text-lg font-bold text-white truncate" data-id="element-357">
                  {user.name}
                </h2>
                <div className="flex items-center gap-1.5" data-id="element-358">
                  <HashIcon className="w-3 h-3 text-slate-500" data-id="element-359" />
                  <span className="text-xs text-slate-500 tabular-nums" data-id="element-360">
                    {user.nmlsId}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-361">
              <div className="flex items-center gap-2" data-id="element-362">
                {counts.licensed > 0 && <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-363">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-364" />
                    <span className="text-xs font-bold text-emerald-400 tabular-nums" data-id="element-365">
                      {counts.licensed}
                    </span>
                  </div>}
                {counts.inProgress > 0 && <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-id="element-366">
                    <ClockIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-367" />
                    <span className="text-xs font-bold text-amber-400 tabular-nums" data-id="element-368">
                      {counts.inProgress}
                    </span>
                  </div>}
              </div>
              <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-369">
                <XIcon className="w-5 h-5" data-id="element-370" />
              </button>
            </div>
          </div>

          {/* Search & Filter */}
          <div className="px-6 py-3 border-b border-slate-800/50 flex flex-col sm:flex-row gap-2.5" data-id="element-371">
            <div className="flex-1 flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 focus-within:border-emerald-500/50 transition-all" data-id="element-372">
              <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-373" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search states…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-374" />
            </div>
            <div className="flex bg-slate-800/60 rounded-lg p-0.5 border border-slate-700/50" data-id="element-375">
              <button onClick={() => setFilter('all')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'all' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-376">
                All
              </button>
              <button onClick={() => setFilter('complete')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-377">
                Licensed
              </button>
              <button onClick={() => setFilter('in_progress')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'in_progress' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-378">
                In Progress
              </button>
              <button onClick={() => setFilter('not_started')} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === 'not_started' ? 'bg-slate-600 text-slate-200' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-379">
                Not Started
              </button>
            </div>
          </div>

          {/* State List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1" data-id="element-380">
            {filteredStates.map(state => {
            const colors = STATUS_COLORS[state.status];
            const isMichigan = state.name === 'Michigan';
            return <button key={state.name} onClick={() => onOpenState(state.name)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-150 hover:bg-opacity-80 group ${colors.bg} ${colors.border}`} data-id="element-381">
                  <span className={`text-sm font-bold w-8 ${colors.text}`} data-id="element-382">
                    {state.abbr}
                  </span>
                  <span className="text-sm text-slate-300 flex-1 text-left flex items-center gap-2" data-id="element-383">
                    {state.name}
                    {isMichigan && <StarIcon className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-384" />}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-385">
                    {state.status === 'complete' && <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-386" />}
                    {state.status === 'in_progress' && <ClockIcon className="w-4 h-4 text-amber-400" data-id="element-387" />}
                    {state.status === 'not_started' && <MinusCircleIcon className="w-4 h-4 text-slate-600" data-id="element-388" />}
                    <ChevronRightIcon className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" data-id="element-389" />
                  </div>
                </button>;
          })}
            {filteredStates.length === 0 && <div className="text-center py-8 text-sm text-slate-500" data-id="element-390">
                No states match your search
              </div>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 flex justify-end" data-id="element-391">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-392">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}